import { chunk } from '@gonfalon/collections/chunk';
import { getMember } from '@gonfalon/openapi/operations/getMember';
import { getMembers } from '@gonfalon/openapi/operations/getMembers';
import { patchMember } from '@gonfalon/openapi/operations/patchMember';
import { patchMembers } from '@gonfalon/openapi/operations/patchMembers';
import { postMembers } from '@gonfalon/openapi/operations/postMembers';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { useMutation } from '@tanstack/react-query';

import { createInfiniteQueryOptions } from '../internal/createInfiniteQueryOptions';
import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

import { getMemberAccessSummaryQuery, getTeamAccessSummaryQuery } from './accessSummaries';

const getProfile = (
  input: Omit<Parameters<typeof getMember>[0], 'id'> = {},
  context: Parameters<typeof getMember>[1] = {},
) =>
  getMember(
    {
      ...input,
      id: 'me',
      query: {
        ...input.query,
        expand: 'roleAttributes',
      },
    },
    context,
  );

export const getProfileQuery = createQueryOptions(getProfile);
export const getMemberQuery = createQueryOptions(getMember);
export const getMembersQuery = createQueryOptions(getMembers);
export const getInfiniteMembersQuery = createInfiniteQueryOptions(getMembers);

function patchMembersSemantically(
  input: Parameters<typeof patchMembers>[0],
  options: Parameters<typeof patchMembers>[1] = {},
) {
  return patchMembers(input, {
    ...options,
    headers: {
      ...options.headers,
      'Content-Type': 'application/json; domain-model=launchdarkly.semanticpatch',
    },
  });
}
export const usePatchMember = createMutationHook(patchMember, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getMemberQuery.partialQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getMemberAccessSummaryQuery.partialQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getTeamAccessSummaryQuery.partialQueryKey() }),
    ]);
  },
});
export const usePatchMembers = createMutationHook(patchMembersSemantically, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getMembersQuery.partialQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getMemberAccessSummaryQuery.partialQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getTeamAccessSummaryQuery.partialQueryKey() }),
    ]);
  },
});
export const useInviteMembers = createMutationHook(postMembers, {
  onSuccess: async () => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: getMembersQuery.partialQueryKey() });
  },
});

export class InviteMembersBatchError extends Error {
  invalidEmails?: string[];
  rateLimitedEmails?: string[];
  /** Addresses that were invited before the failure. Non-empty means a partial success. */
  successfulEmails: string[];

  constructor(
    message: string,
    {
      invalidEmails,
      rateLimitedEmails,
      successfulEmails = [],
    }: { invalidEmails?: string[]; rateLimitedEmails?: string[]; successfulEmails?: string[] } = {},
  ) {
    super(message);
    this.invalidEmails = invalidEmails;
    this.rateLimitedEmails = rateLimitedEmails;
    this.successfulEmails = successfulEmails;
  }
}

/** The endpoint rejects a request carrying more than this many members, and invites none of them. */
export const MAX_MEMBERS_PER_INVITE_REQUEST = 50;

function isFixableBatchError(errStr: string) {
  return ['email_already_exists_in_account', 'email_taken_in_different_account', 'duplicate_emails'].includes(errStr);
}

/**
 * Invites members in chunks, because the endpoint rejects more than 50 per request.
 *
 * Deliberately does not retry. The mutation creates members, and a retry re-runs every chunk from the
 * start, so a late failure would re-post the members earlier chunks already created and turn them
 * into duplicate-email errors.
 */
export function useInviteMembersBatch() {
  return useMutation({
    mutationFn: async (params: Parameters<typeof postMembers>[0]) => {
      const batches = chunk(params.body, MAX_MEMBERS_PER_INVITE_REQUEST);

      const successfulEmails: string[] = [];
      const invalidEmails: string[] = [];
      const rateLimitedEmails: string[] = [];
      const messages: string[] = [];
      // Tracked separately from the collected addresses: an error can arrive without naming any, and
      // keying the throw off the address lists alone would report that batch as a success.
      let failed = false;

      for (const batch of batches) {
        const response = await postMembers({ body: batch });

        if (response.error) {
          failed = true;
          const error = response.error as { code?: string; message?: string; invalid_emails?: unknown };
          const emails = Array.isArray(error.invalid_emails)
            ? error.invalid_emails.filter((email): email is string => typeof email === 'string')
            : [];

          if (error.code !== undefined && isFixableBatchError(error.code) && emails.length > 0) {
            invalidEmails.push(...emails);
          } else if (error.code === 'rate_limited') {
            rateLimitedEmails.push(...emails);
          } else if (error.message !== undefined && error.message !== '') {
            // Anything else keeps the wording the API returned. Previously an error without `invalid_emails`
            // tripped an invariant, and an unrecognized one fell through as though it had succeeded.
            messages.push(error.message);
          } else {
            messages.push('Failed to invite members');
          }

          continue;
        }

        successfulEmails.push(...(response.data?.items.map((member) => member.email) ?? []));
      }

      if (failed) {
        // Prefer the wording the API returned. Failing that, describe what was actually recorded rather than a
        // bare "failed", so a caller reporting a partial success does not contradict itself.
        const message =
          messages[0] ??
          (rateLimitedEmails.length > 0
            ? 'Some invitations were rate limited.'
            : invalidEmails.length > 0
              ? 'Some addresses could not be invited.'
              : 'Some invitations were rejected.');

        throw new InviteMembersBatchError(message, {
          invalidEmails: invalidEmails.length > 0 ? invalidEmails : undefined,
          rateLimitedEmails: rateLimitedEmails.length > 0 ? rateLimitedEmails : undefined,
          successfulEmails,
        });
      }

      return { successfulEmails };
    },
    // Settled, not success: a partial failure still created members, so the list is stale either way.
    onSettled: async () => {
      const queryClient = getQueryClient();
      await queryClient.invalidateQueries({ queryKey: getMembersQuery.partialQueryKey() });
    },
  });
}
